import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, Loader2, X } from 'lucide-react';
import { fetchMembershipTransfers, reviewMembershipTransfer } from '../../../services/MasterAdmin/membershipService';
import ConfirmDialog from '../ui/ConfirmDialog';
import MembershipPanelShell from './MembershipPanelShell';
import MembershipTransferActionsMenu from './MembershipTransferActionsMenu';
import { MembershipError, MembershipLoading, MembershipSuccess, StatusBadge } from './MembershipAlerts';
import {
  formatMembershipDateTime,
  msInput,
  msPrimaryBtn,
  msSecondaryBtn,
  msTableWrap,
  msTd,
  msTh,
} from './membershipUi';

const STATUS_FILTERS = [
  { id: '', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'cancelled', label: 'Cancelled' },
] as const;

function personLabel(user?: { name?: string | null; phone?: string | null; userId?: string } | null) {
  if (!user) return { name: 'Unknown user', phone: '', initial: 'U' };
  const name = user.name || 'Vedika user';
  return {
    name,
    phone: user.phone || '',
    initial: name.charAt(0).toUpperCase(),
  };
}

function isPendingStatus(status?: string | null) {
  return String(status || '').trim().toLowerCase() === 'pending';
}

const MembershipTransfersPanel: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [approveRow, setApproveRow] = useState<any | null>(null);
  const [rejectRow, setRejectRow] = useState<any | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState<string | null>(null);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    try {
      if (!opts?.silent) setLoading(true);
      setError(null);
      setRows(await fetchMembershipTransfers(statusFilter || undefined));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load transfers');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const pendingCount = useMemo(
    () => rows.filter((row) => isPendingStatus(row.status)).length,
    [rows]
  );

  const applyLocalStatus = (transferId: string, status: string, reviewNotes?: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.transferId === transferId
          ? { ...row, status, reviewNotes: reviewNotes ?? row.reviewNotes, reviewedAt: new Date().toISOString() }
          : row
      )
    );
  };

  const review = async (row: any, approve: boolean, reviewNotes?: string) => {
    const transferId = String(row.transferId || '').trim();
    try {
      setBusyId(transferId);
      setError(null);
      setSuccess(null);
      await reviewMembershipTransfer(transferId, { approve, reviewNotes });
      applyLocalStatus(transferId, approve ? 'approved' : 'rejected', reviewNotes);
      setSuccess(approve ? 'Transfer approved.' : 'Transfer rejected.');
      setApproveRow(null);
      setRejectRow(null);
      setRejectReason('');
      setRejectError(null);
      await load({ silent: true });
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Could not update this transfer');
    } finally {
      setBusyId(null);
    }
  };

  const submitReject = async () => {
    const notes = rejectReason.trim();
    if (notes.length < 3) {
      setRejectError('Please enter a rejection reason (at least 3 characters).');
      return;
    }
    if (!rejectRow) return;
    await review(rejectRow, false, notes);
  };

  return (
    <MembershipPanelShell
      title="Transfers"
      actions={
        <button type="button" className={msSecondaryBtn} onClick={() => void load()}>
          Refresh
        </button>
      }
    >
      <MembershipError message={error} />
      <MembershipSuccess message={success} />

      <div className="mb-5 flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((filter) => {
          const active = statusFilter === filter.id;
          return (
            <button
              key={filter.id || 'all'}
              type="button"
              onClick={() => setStatusFilter(filter.id)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                active
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {filter.label}
              {filter.id === 'pending' && pendingCount > 0 && !statusFilter ? ` · ${pendingCount}` : ''}
            </button>
          );
        })}
      </div>

      {loading ? <MembershipLoading /> : null}

      {!loading ? (
        <div className={msTableWrap}>
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className={msTh}>Plan</th>
                <th className={msTh}>From</th>
                <th className={msTh}>To</th>
                <th className={msTh}>What’s transferring</th>
                <th className={msTh}>Requested</th>
                <th className={msTh}>Status</th>
                <th className={`${msTh} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const from = personLabel(row.fromUser);
                const to = personLabel(row.toUser);
                const pending = isPendingStatus(row.status);
                const busy = busyId === row.transferId;
                return (
                  <tr
                    key={row.transferId}
                    className="border-t border-gray-100 transition-colors hover:bg-gray-50/60 dark:border-gray-800 dark:hover:bg-gray-800/40"
                  >
                    <td className={msTd}>
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {row.membership?.planName || row.membership?.plan?.name || 'Membership'}
                      </div>
                      {row.recipientWasNew ? (
                        <div className="mt-1 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                          New Vedika user
                        </div>
                      ) : null}
                      {row.reason ? (
                        <div className="mt-1 max-w-[240px] text-xs text-gray-500 line-clamp-2">
                          Reason: {row.reason}
                        </div>
                      ) : null}
                    </td>
                    <td className={msTd}>
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                          {from.initial}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{from.name}</div>
                          {from.phone ? <div className="text-xs text-gray-400">{from.phone}</div> : null}
                        </div>
                      </div>
                    </td>
                    <td className={msTd}>
                      <div className="flex items-center gap-3">
                        <ArrowRight className="hidden h-4 w-4 flex-shrink-0 text-gray-300 sm:block" />
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                            {to.initial}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">{to.name}</div>
                            {to.phone ? <div className="text-xs text-gray-400">{to.phone}</div> : null}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={msTd}>
                      {row.benefits ? (
                        <div className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-300">
                          <span>{row.benefits.remainingLabel || row.benefits.remainingConsultations}</span>
                          <span>{row.benefits.familyLabel || 'Plan stays with the original member'}</span>
                          <span>{row.benefits.storageLabel || '100 MB free health record storage for the recipient'}</span>
                          {row.benefits.shareNote ? <span>{row.benefits.shareNote}</span> : null}
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={msTd}>
                      <div className="text-xs text-gray-500">
                        {formatMembershipDateTime(row.createdAt) || '—'}
                      </div>
                    </td>
                    <td className={msTd}>
                      <StatusBadge status={row.status} />
                      {row.status === 'rejected' && row.reviewNotes ? (
                        <div className="mt-1 max-w-[200px] text-xs text-red-500 line-clamp-2">
                          {row.reviewNotes}
                        </div>
                      ) : null}
                    </td>
                    <td className={`${msTd} text-right`}>
                      {pending ? (
                        busy ? (
                          <Loader2 className="ml-auto h-4 w-4 animate-spin text-gray-400" />
                        ) : (
                          <MembershipTransferActionsMenu
                            disabled={Boolean(busyId)}
                            onApprove={() => setApproveRow(row)}
                            onReject={() => {
                              setRejectRow(row);
                              setRejectReason('');
                              setRejectError(null);
                            }}
                          />
                        )
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!rows.length ? (
                <tr>
                  <td className={`${msTd} py-10 text-center text-gray-400`} colSpan={7}>
                    No transfer requests.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(approveRow)}
        title="Share remaining consultations?"
        message={
          approveRow
            ? `${personLabel(approveRow.fromUser).name} keeps the ${
                approveRow.membership?.planName || 'membership'
              } plan. Remaining consultation credits will be shared with ${
                personLabel(approveRow.toUser).name
              }, who also gets 100 MB health record storage. Either person can use the credits.`
            : 'Share remaining consultation credits with the recipient? The original member keeps the plan.'
        }
        tone="success"
        confirmLabel="Approve"
        cancelLabel="Cancel"
        busy={Boolean(busyId)}
        onCancel={() => {
          if (!busyId) setApproveRow(null);
        }}
        onConfirm={() => {
          if (approveRow) void review(approveRow, true);
        }}
      />

      {rejectRow ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reject-transfer-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !busyId) setRejectRow(null);
          }}
        >
          <div className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-start gap-3 p-5">
              <div className="min-w-0 flex-1">
                <h3 id="reject-transfer-title" className="text-base font-semibold text-gray-900 dark:text-white">
                  Reject this transfer?
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  Tell {personLabel(rejectRow.fromUser).name} why this request is being declined.
                </p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => {
                    setRejectReason(e.target.value);
                    if (rejectError) setRejectError(null);
                  }}
                  rows={4}
                  placeholder="Rejection reason (required)"
                  className={`${msInput} mt-4 w-full resize-none`}
                  disabled={Boolean(busyId)}
                />
                {rejectError ? <p className="mt-2 text-xs text-red-600">{rejectError}</p> : null}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!busyId) setRejectRow(null);
                }}
                disabled={Boolean(busyId)}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3 dark:border-gray-800 dark:bg-gray-900/40">
              <button
                type="button"
                className={msSecondaryBtn}
                disabled={Boolean(busyId)}
                onClick={() => setRejectRow(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${msPrimaryBtn} bg-red-600 hover:bg-red-700`}
                disabled={Boolean(busyId)}
                onClick={() => void submitReject()}
              >
                {busyId ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Reject transfer
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </MembershipPanelShell>
  );
};

export default MembershipTransfersPanel;
